/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Edit Code Notes component
 */
import _ from 'lodash';
import { $generateHtmlFromNodes } from '@lexical/html';
import { Badge, Button, Dropdown, Modal, Tooltip } from 'antd';
import { $getRoot, LexicalEditor } from 'lexical';
import { MoreOne, Comment } from '@icon-park/react';
import { useState } from 'react';

import Editor from 'lib/lexicalEditor/LexicalEditor';
import EventTracker from 'utils/eventTracker';
import useDeleteCodeTenantData from 'hooks/codeExplorer/mutate/useDeleteCodeTenantData';
import usePatchCodeTenantData from 'hooks/codeExplorer/mutate/usePatchCodeTenantData';
import usePostCodeTenantData from 'hooks/codeExplorer/mutate/usePostCodeTenantData';
import { EVENTS } from 'app-constants/events';

export interface IProps {
	entity: string;
	code: string;
	codeSet: CodeExplorer.ICodeSetSystem;
	codeId: string;
	note?: CodeExplorer.ICodeNotes[];
	showDropdown?: boolean;
	showAddButton?: boolean;
	refetch: () => void;
}

// handling only one note for now
const EditCodeNotes: React.FC<IProps> = ({ code, codeSet, entity, codeId, note, showDropdown = false, showAddButton = false, refetch }) => {
	const patchCodeNote = usePatchCodeTenantData();
	const postCodeNote = usePostCodeTenantData();
	const deleteCodeNote = useDeleteCodeTenantData();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editor, setEditor] = useState<LexicalEditor>();
	const [message, setMessage] = useState<string | null>(_.first(note)?.note);

	const isNoteAdded = !_.isEmpty(note);
	const submitModel = isNoteAdded ? 'patchModel' : 'postModel';

	const handleChange = (val: LexicalEditor) => {
		setEditor(val);
		val?.update(() => {
			const html = $generateHtmlFromNodes(val, null);
			const text = html.match('<img([^>]*[^/])>') ? html : html.replace(/<[^>]+>/g, '').trim();
			setMessage(_.isEmpty(text) ? undefined : html);
		});
	};

	const resetEditor = () => {
		const originalMessage = _.first(note)?.note;
		setMessage(originalMessage);

		if (editor && !originalMessage) {
			editor.update(() => {
				const root = $getRoot();
				root.clear();
			});
		}
	};

	const handleSuccess = () => {
		setIsModalOpen(false);
		refetch();
	};

	const handleDeleteNote = () => {
		const { destroy, update } = Modal.confirm({
			title: 'Delete Note',
			content: 'Are you sure you want to delete this note?',
			okText: 'Yes',
			cancelText: 'Cancel',
			okButtonProps: { danger: true },
			onOk: () => {
				update({ okButtonProps: { loading: true } });
				deleteCodeNote.mutate(
					{ codeId, note: [_.first(note)?.id] },
					{
						onSuccess: () => {
							destroy();
							setMessage(null);
							refetch();
							EventTracker.trackEvent(EVENTS.codeExplorer.internalNoteDeleted(code, codeSet, entity));
						},
						onError: destroy,
					},
				);
				return true;
			},
		});
	};

	const renderModal = () => (
		<Modal
			data-testid='ce-notes-modal'
			title='Internal Note'
			open={isModalOpen}
			destroyOnHidden
			onCancel={() => {
				setIsModalOpen(false);
				resetEditor();
			}}
			width={800}
			footer={[
				<Button data-testid='ce-notes-reset-button' key='cancel' onClick={resetEditor}>
					{'Reset'}
				</Button>,
				<Button
					data-testid='ce-notes-save-button'
					key='save'
					type='primary'
					disabled={_.isEmpty(message) || message === _.first(note)?.note}
					loading={submitModel === 'patchModel' ? patchCodeNote.isPending : postCodeNote.isPending}
					onClick={() => {
						if (submitModel === 'patchModel') {
							patchCodeNote.mutate(
								{ codeId, note: [{ id: _.first(note)?.id, note: message }] },
								{
									onSuccess: () => {
										handleSuccess();
										EventTracker.trackEvent(EVENTS.codeExplorer.internalNoteEdited(code, codeSet, entity));
									},
								},
							);
						} else {
							postCodeNote.mutate(
								{ codeId, note: [{ note: message }] },
								{
									onSuccess: () => {
										handleSuccess();
										EventTracker.trackEvent(EVENTS.codeExplorer.internalNoteAdded(code, codeSet, entity));
									},
								},
							);
						}
					}}
				>
					{'Save'}
				</Button>,
			]}
		>
			<Editor key={codeId} editor={editor} value={message} onChange={handleChange} />
		</Modal>
	);

	// Render dropdown menu or regular edit button based on showDropdown prop
	if (showDropdown) {
		return (
			<>
				<Dropdown
					menu={{
						items: [
							{
								key: 'edit-note',
								label: 'Edit',
								onClick: () => setIsModalOpen(true),
							},
							{
								key: 'delete-note',
								label: 'Delete',
								danger: true,
								onClick: handleDeleteNote,
							},
						],
					}}
					placement='bottomRight'
				>
					<Button data-testid='ce-notes-actions-button' icon={<MoreOne theme='filled' size='22' />} />
				</Dropdown>
				{renderModal()}
			</>
		);
	}

	if (showAddButton) {
		return (
			<>
				<Button data-testid='ce-notes-add-button' type='primary' onClick={() => setIsModalOpen(true)}>
					{'Add Note'}
				</Button>
				{renderModal()}
			</>
		);
	}

	return (
		<>
			<Tooltip title={isNoteAdded ? 'View Internal Note' : 'Add Internal Note'}>
				<Button
					data-testid='ce-notes-tooltip-button'
					type='text'
					icon={
						<Badge dot={!_.isEmpty(note)}>
							<Comment theme='outline' fill='#8C8C8C' size={16} />
						</Badge>
					}
					onClick={() => {
						setIsModalOpen(true);
					}}
				/>
			</Tooltip>
			{renderModal()}
		</>
	);
};

export default EditCodeNotes;
